import React, { useCallback, useId, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from '../hooks/useFiles';
import { usePasteFilesInto } from '../hooks/usePasteFilesInto';
import { Card } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Progress } from '../../../components/ui/progress';
import { cn } from '../../../lib/utils';
import { isPubliclyServableMime } from '../publicServableMime';

/** The generic file-management ceiling — `MAX_FILE_SIZE` in `file_service.py`. */
const DEFAULT_MAX_SIZE_MB = 50;

export interface FileUploadResult {
  id: string;
  original_filename: string;
  mime_type: string;
}

interface FileUploadProps {
  /** Entity reference for attachments in "table_name:uuid" format */
  attachedTo?: string;
  folderId?: string | null;
  onUploadComplete?: (fileId: string, metadata?: FileUploadResult) => void | Promise<void>;
  /**
   * Per-file ceiling in MB, checked BEFORE the upload starts. A function when the
   * ceiling depends on the file — an ingestion dropzone takes a bigger PDF than it
   * takes an image, because the server can split a PDF into parts the AI accepts
   * and cannot split anything else. Returning `undefined` falls back to the
   * default, so a caller whose limits have not loaded yet refuses nothing.
   */
  maxSizeMB?: number | ((file: File) => number | undefined);
  /**
   * One line under the drop hint stating what this zone accepts. Translated by the
   * caller, because only the caller knows which limits apply — and a limit the
   * user learns about by having a file rejected is a limit that was advertised too
   * late (WI 330d4c2f).
   */
  hint?: string;
  accept?: string;
  isPublic?: boolean;
  /** Whether file is part of user's file management (default: true) */
  isUserFile?: boolean;
  /** Compact mode for inline/embedded use */
  compact?: boolean;
  /** Allow selecting multiple files at once */
  multiple?: boolean;
  /** Multi-company (S-pattern): owning company id. null = shared. */
  companyId?: string | null;
  /**
   * Element a paste must land inside for its files to be uploaded — normally the
   * owning dialog's body, so Cmd+V works from anywhere in the form and not only
   * over this dropzone. Omit and the component has no paste behaviour.
   */
  pasteScope?: React.RefObject<HTMLElement | null>;
  /**
   * Which pasted files this control claims. `accept` is NOT applied to a paste
   * automatically, so a single-purpose picker (a resume, a signed PI) uses this
   * to refuse a pasted screenshot it would otherwise upload as that document.
   */
  pasteFilter?: (file: File) => boolean;
}

/**
 * Drag-and-drop / click upload zone.
 *
 * **When `attachedTo` is set, do not render this for a viewer.** Attaching to an
 * entity resolves at EDITOR on that PARENT (`caller_can_attach_to_parent`), so a
 * view-only caller who is offered the dropzone gets a 403 *after* choosing a file.
 * There is deliberately no `disabled` prop: a dropzone whose whole content is an
 * invitation ("Drag and drop files here") has no honest inert state, so the house
 * pattern is for the surrounding surface to withhold it —
 * `{!readOnly && <FileUpload … />}`, as `TaskAttachments` and `EntityAttachments` do.
 */
export function FileUpload({
  attachedTo,
  folderId,
  onUploadComplete,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  hint,
  accept,
  isPublic = false,
  isUserFile = true,
  compact = false,
  multiple = false,
  companyId,
  pasteScope,
  pasteFilter,
}: FileUploadProps) {
  const { t } = useTranslation('file_management');
  const uploadMutation = useUploadFile();
  const inputId = useId();

  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      const limitMB = (typeof maxSizeMB === 'function' ? maxSizeMB(file) : maxSizeMB) ?? DEFAULT_MAX_SIZE_MB;
      if (file.size > limitMB * 1024 * 1024) {
        return t('maxSizeError', { size: limitMB });
      }
      if (file.size === 0) {
        return t('emptyFileError');
      }
      // A PUBLIC target is bounded by PUBLICLY_SERVABLE_MIME
      // (`modules/file_management/public_asset_mime.py`) — an SVG stored public is
      // script execution on our own origin, so `FileService.upload` refuses it.
      // Checked here rather than left to the caller's `accept`, because `accept`
      // filters the OS picker ONLY: `handleDrop` hands every dataTransfer file
      // straight through and the paste path is unfiltered too, so a literal alone
      // would trade a greyed-out file for an unexplained 400 on the other two
      // gestures. A private target is unaffected — an SVG attachment is an
      // ordinary file.
      if (isPublic && !isPubliclyServableMime(file.type)) {
        return t('notPubliclyServable');
      }
      return null;
    },
    [maxSizeMB, isPublic, t]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);

      try {
        const result = await uploadMutation.mutateAsync({
          file,
          folder_id: folderId || undefined,
          is_public: isPublic,
          is_user_file: isUserFile,
          attached_to: attachedTo,
          company_id: companyId ?? null,
        });
        const data = result.data;
        await onUploadComplete?.(data.id, {
          id: data.id,
          original_filename: data.original_filename,
          mime_type: data.mime_type,
        });
      } catch (err: unknown) {
        const apiError = err as { error?: { message?: string } };
        const message = apiError?.error?.message || t('uploadError');
        setError(message);
        console.error('Upload failed:', err);
      }
    },
    [validateFile, attachedTo, folderId, isPublic, isUserFile, companyId, uploadMutation, onUploadComplete, t]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        await uploadFile(file);
      }
    },
    [uploadFile]
  );

  // A pasted screenshot takes the same route as the picker: `multiple` decides
  // whether a multi-file paste uploads all of them or just the first, so the
  // control can never accept more than its caller asked for.
  usePasteFilesInto({
    scopeRef: pasteScope,
    onFiles: (files) => {
      const mine = pasteFilter ? files.filter(pasteFilter) : files;
      if (mine.length === 0) return;
      if (multiple) void uploadFiles(mine);
      else void uploadFile(mine[0]);
    },
    disabled: uploadMutation.isPending,
  });

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        if (multiple) {
          uploadFiles(files);
        } else {
          uploadFile(files[0]);
        }
      }
    },
    [uploadFile, uploadFiles, multiple]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        // Reset input value immediately so the same file can be re-selected
        e.target.value = '';
        if (multiple) {
          uploadFiles(fileArray);
        } else {
          uploadFile(fileArray[0]);
        }
      }
    },
    [uploadFile, uploadFiles, multiple]
  );

  const isUploading = uploadMutation.isPending;

  return (
    <div onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      {/*
        The file input is the control, not a hidden implementation detail. It was
        `display:none` with the whole picker hanging off a `<div onClick>`, which
        took it out of the tab order entirely: a keyboard-only user could not
        start a document import at all — not slowly, at all (WI ACC-J1).

        `sr-only` keeps it visually hidden but focusable, so Tab reaches it and
        Enter/Space open the picker through native semantics — no `role="button"`
        mimicry, no key handler to get wrong, and a screen reader announces a real
        file input. It sits OUTSIDE the Card so the Card can be its `peer`: focus
        the input and the dashed card takes the focus ring, which is the only way
        a visually-hidden control can show where focus is.
      */}
      <input
        id={inputId}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="peer sr-only"
        disabled={isUploading}
        aria-label={t('chooseFiles')}
      />
      <Card
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !isUploading && document.getElementById(inputId)?.click()}
        className={cn(
          'text-center border-dashed transition-colors',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
          compact ? 'p-3 border' : 'p-6 border-2',
          isUploading
            ? 'cursor-wait opacity-70'
            : 'cursor-pointer hover:border-primary hover:bg-accent',
          dragActive && 'border-primary bg-accent'
        )}
      >
        {isUploading ? (
          <div className={cn('flex items-center justify-center', compact ? 'gap-2' : 'flex-col')}>
            <Loader2 className={cn('text-primary animate-spin', compact ? 'h-5 w-5' : 'h-10 w-10 mb-2')} />
            <p className={cn('text-muted-foreground', compact && 'text-sm')}>{t('uploading')}</p>
          </div>
        ) : (
          <>
            <div className={cn('flex items-center justify-center', compact ? 'gap-2' : 'flex-col')}>
              <Upload className={cn('text-muted-foreground', compact ? 'h-5 w-5' : 'h-10 w-10 mb-2')} />
              <p className={cn('text-muted-foreground', compact && 'text-sm')}>{t('dragDropHint')}</p>
            </div>
            {/* Its own line rather than inside the flex row above, so a compact
                zone at 375px stacks the limit under the invitation instead of
                pushing it off the edge. */}
            {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
          </>
        )}
      </Card>

      {isUploading && (
        <Progress className="mt-2" value={undefined} aria-label={t('uploadProgress')} />
      )}

      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
